/**
 * AudioEngine - Gestion du contexte Web Audio via Tone.js
 * Avec bus d'effets (delay, etc.)
 */

import * as Tone from 'tone';
import { Delay } from './effects/Delay.js';
import { Reverb } from './effects/Reverb.js';
import { Chorus } from './effects/Chorus.js';
import { Phaser } from './effects/Phaser.js';
import { Flanger } from './effects/Flanger.js';
import { Bitcrusher } from './effects/Bitcrusher.js';
import { Tremolo } from './effects/Tremolo.js';
import { MasterDistortion } from './effects/MasterDistortion.js';
import { EQ } from './effects/EQ.js';
import { Compressor } from './effects/Compressor.js';
import { Mixer } from './Mixer.js';

export class AudioEngine {
    constructor(options = {}) {
        this.isStarted = false;
        this.performance = options.performance || { lowPower: false };

        // Callbacks for effect activity changes (for UI updates)
        this.effectActivityCallbacks = [];

        // Compresseur master (avant la sortie finale)
        this.compressor = new Compressor(this);
        this.compressor.output.toDestination();

        // EQ master (avant le compresseur)
        this.eq = new EQ();
        this.eq.connect(this.compressor.input);

        // Volume master (avant l'EQ)
        this.masterVolume = new Tone.Volume(0);
        this.masterVolume.connect(this.eq.input);

        // Bus de delay (collecteur des sends)
        this.delaySend = new Tone.Gain(1);
        this.delay = new Delay(this);
        this.delaySend.connect(this.delay.input);
        this.delay.connectTo(this.masterVolume);

        // Bus de reverb (collecteur des sends)
        this.reverbSend = new Tone.Gain(1);
        this.reverb = new Reverb(this);
        this.reverbSend.connect(this.reverb.input);
        this.reverb.connectTo(this.masterVolume);

        // Bus de chorus (collecteur des sends)
        this.chorusSend = new Tone.Gain(1);
        this.chorus = new Chorus();
        this.chorusSend.connect(this.chorus.input);
        this.chorus.connectTo(this.masterVolume);

        // Bus de phaser
        this.phaserSend = new Tone.Gain(1);
        this.phaser = new Phaser();
        this.phaserSend.connect(this.phaser.input);
        this.phaser.connectTo(this.masterVolume);

        // Bus de flanger
        this.flangerSend = new Tone.Gain(1);
        this.flanger = new Flanger();
        this.flangerSend.connect(this.flanger.input);
        this.flanger.connectTo(this.masterVolume);

        // Bus de bitcrusher
        this.crusherSend = new Tone.Gain(1);
        this.bitcrusher = new Bitcrusher();
        this.crusherSend.connect(this.bitcrusher.input);
        this.bitcrusher.connectTo(this.masterVolume);

        // Bus de tremolo
        this.tremoloSend = new Tone.Gain(1);
        this.tremolo = new Tremolo();
        this.tremoloSend.connect(this.tremolo.input);
        this.tremolo.connectTo(this.masterVolume);

        // Bus de distortion
        this.distortionSend = new Tone.Gain(1);
        this.distortion = new MasterDistortion();
        this.distortionSend.connect(this.distortion.input);
        this.distortion.connectTo(this.masterVolume);

        // === MIXER ===
        // Les instruments se connectent aux canaux du mixer
        // Le mixer gère volume, mute, solo ET sends (post-fader)
        this.mixer = new Mixer(
            this.masterVolume,
            this.delaySend,
            this.reverbSend,
            this.chorusSend,
            this.phaserSend,
            this.flangerSend,
            this.crusherSend,
            this.tremoloSend,
            this.distortionSend,
            {
                metersEnabled: !this.performance.lowPower,
                onSendChange: (effectKey, total) => this._handleSendChange(effectKey, total)
            }
        );

        // Créer les canaux pour chaque instrument
        this.mixer.createChannel('tb303_1');
        this.mixer.createChannel('tb303_2');
        this.mixer.createChannel('tr808');
        this.mixer.createChannel('tr909');
        this.mixer.createChannel('minimoog');
        this.mixer.createChannel('ms20');
        this.mixer.createChannel('jupiter8');
        this.mixer.createChannel('theremin');
        this.mixer.createChannel('arp2600');
        this.mixer.createChannel('juno106');
        this.mixer.createChannel('sh101');
        this.mixer.createChannel('minimod');
    }

    /**
     * Obtient le volume master
     */
    getMasterVolume() {
        return this.masterVolume;
    }

    /**
     * Démarre le contexte audio (nécessite une interaction utilisateur)
     */
    async start() {
        if (!this.isStarted) {
            await Tone.start();
            this.isStarted = true;
        }
        return this.isStarted;
    }

    /**
     * Obtient la destination de sortie (pour connecter les instruments)
     */
    getDestination() {
        return this.masterVolume;
    }

    /**
     * Obtient le bus de delay (pour envoyer du signal au delay)
     */
    getDelaySend() {
        return this.delaySend;
    }

    /**
     * Obtient l'effet delay
     */
    getDelay() {
        return this.delay;
    }

    /**
     * Obtient le bus de reverb
     */
    getReverbSend() {
        return this.reverbSend;
    }

    /**
     * Obtient l'effet reverb
     */
    getReverb() {
        return this.reverb;
    }

    /**
     * Définit le volume master
     * @param {number} db - Volume en décibels
     */
    setMasterVolume(db) {
        this.masterVolume.volume.value = db;
    }

    /**
     * Définit le niveau d'envoi vers le delay
     * @param {number} level - Niveau 0-1
     */
    setDelaySendLevel(level) {
        this.delaySend.gain.value = level;
    }

    /**
     * Obtient le contexte audio Tone.js
     */
    getContext() {
        return Tone.getContext();
    }

    /**
     * Obtient le compresseur master
     */
    getCompressor() {
        return this.compressor;
    }

    /**
     * Obtient l'EQ master
     */
    getEQ() {
        return this.eq;
    }

    /**
     * Obtient l'effet chorus
     */
    getChorus() {
        return this.chorus;
    }

    /**
     * Obtient le bus de chorus
     */
    getChorusSend() {
        return this.chorusSend;
    }

    /**
     * Obtient l'effet phaser
     */
    getPhaser() {
        return this.phaser;
    }

    /**
     * Obtient l'effet flanger
     */
    getFlanger() {
        return this.flanger;
    }

    /**
     * Obtient l'effet bitcrusher
     */
    getBitcrusher() {
        return this.bitcrusher;
    }

    /**
     * Obtient l'effet tremolo
     */
    getTremolo() {
        return this.tremolo;
    }

    /**
     * Obtient l'effet distortion master
     */
    getDistortion() {
        return this.distortion;
    }

    /**
     * Obtient le mixer
     */
    getMixer() {
        return this.mixer;
    }

    /**
     * Obtient l'entrée d'un canal du mixer
     * @param {string} channelId - ID du canal ('tb303_1', 'tb303_2', 'tr808', 'tr909')
     */
    getMixerChannelInput(channelId) {
        return this.mixer.getChannelInput(channelId);
    }

    /**
     * Obtient le temps actuel du contexte audio
     */
    get now() {
        return Tone.now();
    }

    /**
     * Gère le changement d'activité des sends pour activer/désactiver les effets
     * @param {string} effectKey - Clé de l'effet (delay, reverb, chorus, etc.)
     * @param {number} total - Somme des sends pour cet effet
     */
    _handleSendChange(effectKey, total) {
        const active = total > 0.001;
        const effectMap = {
            delay: this.delay,
            reverb: this.reverb,
            chorus: this.chorus,
            phaser: this.phaser,
            flanger: this.flanger,
            crusher: this.bitcrusher,
            tremolo: this.tremolo,
            distortion: this.distortion
        };
        const effect = effectMap[effectKey];
        if (effect && typeof effect.setActive === 'function') {
            effect.setActive(active);
        }

        // Notify registered callbacks (for UI updates)
        for (const callback of this.effectActivityCallbacks) {
            try {
                callback(effectKey, active);
            } catch (e) {
                console.warn('Effect activity callback error:', e);
            }
        }
    }

    /**
     * Register a callback for effect activity changes
     * @param {Function} callback - Function(effectKey, active) called when effect activity changes
     * @returns {Function} Unregister function
     */
    onEffectActivity(callback) {
        this.effectActivityCallbacks.push(callback);
        return () => {
            const index = this.effectActivityCallbacks.indexOf(callback);
            if (index !== -1) {
                this.effectActivityCallbacks.splice(index, 1);
            }
        };
    }

    /**
     * Vérifie si un effet est actif (reçoit du signal)
     * @param {string} effectKey - Clé de l'effet
     * @returns {boolean}
     */
    isEffectActive(effectKey) {
        return this.mixer.getEffectiveSendTotal(effectKey) > 0.001;
    }
}
